import { Effect, Layer, Schema } from 'effect';

import { CookieKeys, DefaultCookieOptions, ORDER_SHIPMENT_MAX_AGE } from '../../../config/cookies';

import { ErrorTag } from '@vs-shop/engine/data/error';
import {
  GetOrderShipmentError,
  OrderShipment as EngineOrderShipment,
  OrderShipmentStorageJsonSchema,
  SetOrderShipmentError,
} from '@vs-shop/engine/service/orderShipment/OrderShipment';
import type { OrderShipmentStorageT } from '@vs-shop/engine/service/orderShipment/OrderShipment';
import { CookiesStore } from '@vs-shop/engine/service/cookies/CookiesStore';

export { GetOrderShipmentError, SetOrderShipmentError };
export type { OrderShipmentStorageT };

const make = Effect.gen(function* () {
  const cookieService = yield* CookiesStore;
  const key = CookieKeys.ORDER_SHIPMENT;

  return {
    getOrderShipment: Effect.gen(function* () {
      const cookieValue = yield* cookieService.get(key).pipe(
        Effect.catchTags({
          [ErrorTag.CookieGetError]: (error) =>
            new GetOrderShipmentError({
              message: `Failed to get order shipment: ${error.message}`,
              cause: error,
            }),
          [ErrorTag.CookieAccessError]: (error) =>
            new GetOrderShipmentError({
              message: `Failed to access order shipment cookies: ${error.message}`,
              cause: error,
            }),
        })
      );

      if (cookieValue === null) {
        return null;
      }

      return yield* Schema.decodeEffect(OrderShipmentStorageJsonSchema)(cookieValue).pipe(
        Effect.catch(
          (parseError) =>
            new GetOrderShipmentError({
              message: 'Failed to parse order shipment data',
              cause: parseError,
            })
        )
      );
    }),

    setOrderShipment: (orderShipment: OrderShipmentStorageT) =>
      Effect.gen(function* () {
        const cookieOptions = {
          ...DefaultCookieOptions,
          expires: new Date(Date.now() + ORDER_SHIPMENT_MAX_AGE * 1000),
        };

        const serialized = yield* Schema.encodeEffect(OrderShipmentStorageJsonSchema)(
          orderShipment
        );

        yield* cookieService.set(key, serialized, cookieOptions).pipe(
          Effect.catchTags({
            [ErrorTag.CookieSetError]: (error) =>
              new SetOrderShipmentError({
                message: `Failed to set order shipment: ${error.message}`,
                cause: error,
              }),
            [ErrorTag.CookieAccessError]: (error) =>
              new SetOrderShipmentError({
                message: `Failed to access order shipment cookies: ${error.message}`,
                cause: error,
              }),
          })
        );

        return;
      }),

    removeOrderShipment: Effect.gen(function* () {
      const cookieOptions = {
        path: '/',
      };

      return yield* cookieService.remove(key, cookieOptions).pipe(
        Effect.catchTags({
          [ErrorTag.CookieRemoveError]: (error) =>
            new SetOrderShipmentError({
              message: `Failed to remove order shipment: ${error.message}`,
              cause: error,
            }),
          [ErrorTag.CookieAccessError]: (error) =>
            new SetOrderShipmentError({
              message: `Failed to access order shipment cookies: ${error.message}`,
              cause: error,
            }),
        })
      );
    }),
  };
});

export const OrderShipmentLive = Layer.effect(EngineOrderShipment, make);
EngineOrderShipment.Live = OrderShipmentLive;

export { EngineOrderShipment as OrderShipment };
